import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  CheckCircle,
  Compass,
  Handshake,
  Lightbulb,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';

type Locale = 'en' | 'el';

type HeroContent = {
  eyebrow: string;
  title: string;
  description: string[];
  primaryCta: string;
  secondaryCta: string;
  valueProps: {
    title: string;
    subtitle: string;
  }[];
};

type HighlightSection = {
  eyebrow: string;
  heading: string;
  description: string;
  bullets: string[];
  note: string;
};

type ProcessStep = {
  title: string;
  description: string;
  bullets: string[];
};

type SectionCopy = {
  eyebrow: string;
  heading: string;
  description: string;
};

type BulletSection = SectionCopy & {
  bullets: string[];
  note?: string;
};

type TeamMember = {
  name: string;
  image: string;
  role: string;
  description: string;
};

type ValueItem = {
  title: string;
  description: string;
};

type ClosingContent = {
  eyebrow: string;
  title: string;
  description: string;
  lead: string;
  bullets: string[];
  primaryCta: string;
  secondaryCta: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'The PixelPro team',
    title: 'A small team that builds serious digital work.',
    description: [
      'PixelPro is an independent team of design, development, and content working closely with clients and covering the whole journey, from idea to delivery.',
      "We don't operate like an impersonal agency. We get inside the project, understand what it needs, and build it properly.",
    ],
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See our services',
    valueProps: [
      {
        title: 'Built right',
        subtitle: 'STRUCTURE, CODE, AND PERFORMANCE',
      },
      {
        title: 'Steady partnership',
        subtitle: 'NOT ONE-OFF PROJECTS, BUT CONTINUITY',
      },
      {
        title: 'Organized from the start',
        subtitle: 'CLEAR STEPS, CLEAR DECISIONS',
      },
    ],
  },
  el: {
    eyebrow: 'Η ομάδα PixelPro',
    title: 'Μια ομάδα που χτίζει σοβαρά ψηφιακά έργα.',
    description: [
      'Στην PixelPro χτίζουμε ιστοσελίδες με σωστή δομή, όχι μόνο design.',
      'Δίνουμε βάση στην απόδοση και στη δυνατότητα εξέλιξης.',
    ],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε τις υπηρεσίες μας',
    valueProps: [
      {
        title: 'Χτισμένο σωστά',
        subtitle: 'ΔΟΜΗ, ΚΩΔΙΚΑΣ ΚΑΙ ΑΠΟΔΟΣΗ',
      },
      {
        title: 'Σταθερή συνεργασία',
        subtitle: 'ΟΧΙ ΕΡΓΑ ΤΟΥ ΜΗΝΑ, ΑΛΛΑ ΣΥΝΕΧΕΙΑ',
      },
      {
        title: 'Οργάνωση από την αρχή',
        subtitle: 'ΞΕΚΑΘΑΡΑ ΒΗΜΑΤΑ, ΚΑΘΑΡΕΣ ΑΠΟΦΑΣΕΙΣ',
      },
    ],
  },
};

const traitsCopy: Record<Locale, HighlightSection> = {
  en: {
    eyebrow: 'What defines us',
    heading: 'What defines us',
    description: 'Clear thinking before implementation starts.',
    bullets: [
      'Solutions that can be maintained and evolved',
      'Direct communication, without layers or middlemen',
      'Clarity before implementation begins',
    ],
    note: "We don't promise everything. We take on what we can stand behind with results.",
  },
  el: {
    eyebrow: 'ΦΙΛΟΣΟΦΙΑ',
    heading: 'Τι μας χαρακτηρίζει',
    description: 'Καθαρή σκέψη πριν ξεκινήσει η υλοποίηση',
    bullets: [
      'Λύσεις που μπορούν να συντηρηθούν και να εξελιχθούν',
      'Άμεση επικοινωνία, χωρίς layers και μεσάζοντες',
      'Σαφήνεια πριν ξεκινήσει η υλοποίηση',
    ],
    note: 'Δεν υποσχόμαστε τα πάντα. Αναλαμβάνουμε ό,τι μπορούμε να στηρίξουμε με πράξεις.',
  },
};

const processIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'Process',
    heading: 'How we work',
    description: 'We work organized, but not heavy. Every collaboration starts with understanding and continues with clear steps.',
  },
  el: {
    eyebrow: 'ΔΙΑΔΙΚΑΣΙΑ',
    heading: 'Πώς δουλεύουμε',
    description: 'Δουλεύουμε οργανωμένα, αλλά όχι βαριά. Κάθε συνεργασία ξεκινά με κατανόηση των αναγκών και συνεχίζει με ξεκάθαρα βήματα.',
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Align goals and priorities',
      description: 'Before the project starts we discuss goals and priorities.',
      bullets: ['Clear targets', 'Shared priorities', 'Aligned expectations'],
    },
    {
      title: 'Break the work into parts',
      description: 'We split work into small, manageable pieces.',
      bullets: ['Structured scope', 'Practical milestones', 'Simple next steps'],
    },
    {
      title: 'Deliver with visibility',
      description: 'We deliver gradually with continuous progress visibility.',
      bullets: ['Regular check-ins', 'Transparent progress', 'Iterative delivery'],
    },
  ],
  el: [
    {
      title: 'Συζητάμε στόχους και προτεραιότητες',
      description: 'Συζητάμε στόχους και προτεραιότητες πριν ξεκινήσει το έργο.',
      bullets: ['Σαφείς στόχοι', 'Κοινές προτεραιότητες', 'Ξεκάθαρες προσδοκίες'],
    },
    {
      title: 'Σπάμε τη δουλειά σε κομμάτια',
      description: 'Σπάμε τη δουλειά σε μικρά, διαχειρίσιμα κομμάτια.',
      bullets: ['Οργανωμένο scope', 'Πρακτικά milestones', 'Απλά επόμενα βήματα'],
    },
    {
      title: 'Παραδίδουμε με εικόνα προόδου',
      description: 'Παραδίδουμε σταδιακά, με συνεχή εικόνα της προόδου.',
      bullets: ['Σταθερή επικοινωνία', 'Διαφάνεια', 'Συνεχής παράδοση'],
    },
  ],
};

const processNoteCopy: Record<Locale, string> = {
  en: 'Documentation, clean structure, and training are not extras. They are part of the work.',
  el: 'Η τεκμηρίωση, η καθαρή δομή και η εκπαίδευση δεν είναι έξτρα. Είναι μέρος της δουλειάς.',
};

const philosophyCopy: Record<Locale, BulletSection> = {
  en: {
    eyebrow: 'Philosophy',
    heading: 'Our philosophy',
    description: 'We believe good work shows over time, not just at launch.',
    bullets: [
      'The right foundation',
      'The reasoning behind every decision',
      'The ability for a project to continue without dependencies',
    ],
  },
  el: {
    eyebrow: 'ΦΙΛΟΣΟΦΙΑ',
    heading: 'Η φιλοσοφία μας',
    description: 'Πιστεύουμε ότι η καλή δουλειά φαίνεται στη διάρκεια. Όχι μόνο στο launch.',
    bullets: [
      'στη σωστή βάση',
      'στη λογική πίσω από κάθε απόφαση',
      'στη δυνατότητα του project να συνεχίσει χωρίς εξαρτήσεις',
    ],
  },
};

const scopeCopy: Record<Locale, BulletSection> = {
  en: {
    eyebrow: 'Services',
    heading: 'What we take on',
    description: 'We cover everything a modern digital project needs to stand properly.',
    bullets: [
      'Websites and web applications',
      'E-shops and online sales functionality',
      'Mobile apps and custom tools',
      'Branding, content, and post-launch support',
    ],
    note: "We don't sell packages. We build solutions around real needs.",
  },
  el: {
    eyebrow: 'ΥΠΗΡΕΣΙΕΣ',
    heading: 'Τι αναλαμβάνουμε',
    description: 'Καλύπτουμε ό,τι χρειάζεται ένα σύγχρονο ψηφιακό project για να σταθεί σωστά.',
    bullets: [
      'Ιστοσελίδες και web εφαρμογές',
      'E-shops και λειτουργίες για online πωλήσεις',
      'Mobile εφαρμογές και custom εργαλεία',
      'Branding, περιεχόμενο και υποστήριξη μετά το launch',
    ],
    note: 'Δεν πουλάμε πακέτα. Χτίζουμε λύσεις πάνω σε πραγματικές ανάγκες.',
  },
};

const teamMembers = [
  { key: 'alexandros', name: 'Alexandros Alali', image: '/images/team/alexandros.svg' },
  { key: 'thanos', name: 'Thanos Mpalaris', image: '/images/team/thanos.svg' },
  { key: 'kyriakos', name: 'Kyriakos Semertzidis', image: '/images/team/kyriakos.svg' },
] as const;

type TeamKey = typeof teamMembers[number]['key'];

const teamCopy: Record<Locale, { eyebrow: string; heading: string; description: string; members: Record<TeamKey, Omit<TeamMember, 'name' | 'image'>> }> = {
  en: {
    eyebrow: 'Team',
    heading: 'The team',
    description: "We handle every project ourselves. We don't outsource critical parts.",
    members: {
      alexandros: {
        role: 'Product & Web Development',
        description:
          'Designs and delivers the experience of each project, from design to functionality. Responsible for frontend, architecture, and overall quality.',
      },
      thanos: {
        role: 'Web Development & Partnerships',
        description:
          'Handles the logic behind applications, databases, and integrations. Stays close to clients and turns needs into working solutions.',
      },
      kyriakos: {
        role: 'Application Development & Finance',
        description:
          'Looks after the financial side of the team and contributes to application development, focusing on system logic and functionality.',
      },
    },
  },
  el: {
    eyebrow: 'ΟΜΑΔΑ',
    heading: 'Η ομάδα',
    description: 'Κάθε project το δουλεύουμε οι ίδιοι. Δεν αναθέτουμε κρίσιμα κομμάτια αλλού.',
    members: {
      alexandros: {
        role: 'Product & Web Development',
        description:
          'Σχεδιάζει και υλοποιεί την εμπειρία κάθε project, από το design μέχρι τη λειτουργία του. Έχει την ευθύνη για το frontend, την αρχιτεκτονική και τη συνολική ποιότητα των εφαρμογών που παραδίδουμε.',
      },
      thanos: {
        role: 'Web Development & Συνεργασίες',
        description:
          'Αναλαμβάνει τη λογική πίσω από τις εφαρμογές, τις βάσεις δεδομένων και τις συνδέσεις. Παράλληλα βρίσκεται σε επαφή με πελάτες, οργανώνει τις ανάγκες τους και βοηθά να μετατρέπονται σε λειτουργικές λύσεις.',
      },
      kyriakos: {
        role: 'Ανάπτυξη Εφαρμογών & Οικονομικά',
        description:
          'Φροντίζει την οικονομική εικόνα της ομάδας και συμμετέχει στην ανάπτυξη εφαρμογών. Εξελίσσεται κυρίως στον τομέα της λογικής και της λειτουργίας πίσω από τα projects και τα συστήματα.',
      },
    },
  },
};

const valuesCopy: Record<Locale, { eyebrow: string; heading: string; description: string; values: ValueItem[] }> = {
  en: {
    eyebrow: 'Principles',
    heading: 'Our principles',
    description: 'Not slogans. Our way of working.',
    values: [
      {
        title: 'Honesty',
        description: 'We say from the start what can and cannot be done.',
      },
      {
        title: 'Quality',
        description: 'We deliver only what we would use ourselves.',
      },
      {
        title: 'Collaboration',
        description: 'We work with you, not just for you.',
      },
      {
        title: 'Continuous learning',
        description: 'We keep improving, technically and practically.',
      },
    ],
  },
  el: {
    eyebrow: 'ΑΡΧΕΣ',
    heading: 'Οι αρχές μας',
    description: 'Δεν είναι slogans. Είναι τρόπος δουλειάς.',
    values: [
      {
        title: 'Ειλικρίνεια',
        description: 'Λέμε από την αρχή τι μπορεί και τι δεν μπορεί να γίνει.',
      },
      {
        title: 'Ποιότητα',
        description: 'Παραδίδουμε μόνο ό,τι θα χρησιμοποιούσαμε και εμείς.',
      },
      {
        title: 'Συνεργασία',
        description: 'Δουλεύουμε μαζί σας, όχι απλώς για εσάς.',
      },
      {
        title: 'Συνεχής μάθηση',
        description: 'Βελτιωνόμαστε συνεχώς, τεχνικά και πρακτικά.',
      },
    ],
  },
};

const locationCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'Location',
    heading: 'Where we are',
    description:
      'We work mainly with teams in Greece and the United Kingdom, with experience on international projects. Communication and availability adapt to each collaboration.',
  },
  el: {
    eyebrow: 'ΤΟΠΟΘΕΣΙΑ',
    heading: 'Πού βρισκόμαστε',
    description:
      'Δουλεύουμε κυρίως με ομάδες στην Ελλάδα και το Ηνωμένο Βασίλειο, με εμπειρία και σε διεθνή projects. Η επικοινωνία και η διαθεσιμότητα προσαρμόζονται στις ανάγκες κάθε συνεργασίας.',
  },
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    eyebrow: 'Next steps',
    title: 'Start with us',
    description: 'If you have an idea, a project, or something that is not working, talk to us.',
    lead: "We'll clearly tell you:",
    bullets: [
      'Whether we can help',
      'How we would approach it',
      'What the next steps are',
    ],
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See projects and results',
  },
  el: {
    eyebrow: 'ΕΠΟΜΕΝΟ ΒΗΜΑ',
    title: 'Ξεκινήστε μαζί μας',
    description: 'Αν έχετε μια ιδέα, ένα project ή κάτι που δεν δουλεύει όπως θα έπρεπε, μιλήστε μαζί μας.',
    lead: 'Θα σας πούμε ξεκάθαρα:',
    bullets: [
      'αν μπορούμε να βοηθήσουμε',
      'πώς θα το προσεγγίσουμε',
      'ποια είναι τα επόμενα βήματα',
    ],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε έργα και αποτελέσματα',
  },
};

const valueIcons: LucideIcon[] = [ShieldCheck, Sparkles, Handshake, Lightbulb];

export default function About() {
  const location = useLocation();
  const { language } = useLanguage();
  const locale: Locale = language === 'el' ? 'el' : 'en';
  const localizedPath = useLocalizedPath();
  const meta = getPageMeta('about', undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);

  const hero = heroCopy[locale];
  const traits = traitsCopy[locale];
  const processIntro = processIntroCopy[locale];
  const processSteps = processCopy[locale];
  const philosophy = philosophyCopy[locale];
  const scope = scopeCopy[locale];
  const team = teamCopy[locale];
  const teamCards = teamMembers.map((member) => ({
    ...member,
    ...team.members[member.key],
  }));
  const values = valuesCopy[locale];
  const locationSection = locationCopy[locale];
  const closing = closingCopy[locale];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="relative min-h-screen bg-black text-white">
      <Seo title={meta.title} description={meta.description} url={canonicalUrl} hreflang={hreflang} />
      <PixelBackground />

      <div className="relative z-10 flex min-h-screen flex-col">
        <Header variant="simple" />

        <main id="main-content" className="flex-1">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" />
            <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" />

            <Container className="relative pt-4 pb-4 lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
              <div className="grid grid-cols-1 gap-10 sm:gap-12 lg:gap-14 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:items-center">
                <div className="space-y-8">
                  <div className="space-y-5">
                    <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                      {hero.eyebrow}
                    </p>
                    <Display
                      variant="h1"
                      as="h1"
                      className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                    >
                      {hero.title}
                    </Display>
                  </div>

                  <div className="space-y-4">
                    {hero.description.map((paragraph) => (
                      <p key={paragraph} className="text-base sm:text-lg leading-relaxed text-white/70">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Button
                      as="button"
                      onClick={() => window.navigateToConsultation?.()}
                      trackCta={{ text: hero.primaryCta, section: 'about_hero', variant: 'primary' }}
                      variant="primary"
                      size="lg"
                      className="w-full justify-center whitespace-nowrap sm:w-auto px-10 py-4 text-lg"
                    >
                      {hero.primaryCta}
                    </Button>
                    <Button
                      as="a"
                      href={localizedPath('/services')}
                      trackCta={{ text: hero.secondaryCta, section: 'about_hero', variant: 'secondary' }}
                      variant="secondary"
                      size="lg"
                      className="w-full justify-center whitespace-nowrap sm:w-auto px-10 py-4 text-lg"
                    >
                      {hero.secondaryCta}
                    </Button>
                  </div>
                </div>

                <div className="relative mt-4 sm:mt-6 lg:mt-0">
                  <div className="pointer-events-none absolute -top-10 -right-14 h-32 w-32 rounded-full bg-brand-highlight/20 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-12 -left-16 h-36 w-36 rounded-full bg-white/10 blur-3xl" />
                  <div className="relative w-full rounded-3xl border border-white/15 bg-black/70 p-6 shadow-glass backdrop-blur-xl sm:max-w-md sm:p-7 lg:ml-auto lg:p-9">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-brand-highlight/70">
                      {traits.eyebrow}
                    </p>
                    <h2 className="mt-3 text-lg font-semibold text-white sm:text-xl md:text-2xl">{traits.heading}</h2>
                    <p className="mt-3 text-sm text-white/70">{traits.description}</p>
                    <ul className="mt-4 space-y-2 text-sm text-white/70">
                      {traits.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 text-xs text-white/55">{traits.note}</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
                {hero.valueProps.map((item) => (
                  <div key={item.title} className="space-y-1 py-2 sm:px-6">
                    <p className="text-base font-semibold text-white">{item.title}</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                      {item.subtitle}
                    </p>
                  </div>
                ))}
              </div>
            </Container>
          </HeroSection>

          <Section id="process" className="relative isolate">
            <div className="absolute inset-0 geometric-pattern opacity-20" aria-hidden="true" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4 text-center md:mx-auto">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {processIntro.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{processIntro.heading}</h2>
                <p className="text-lg text-white/70">{processIntro.description}</p>
              </div>

              <div className="mt-16 grid gap-8 lg:grid-cols-3">
                {processSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="group flex h-full flex-col gap-4 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-lg font-semibold text-white transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                    <ul className="mt-2 space-y-2 text-sm text-white/70">
                      {step.bullets.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-highlight/70" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <p className="mt-10 text-center text-sm text-white/60">{processNoteCopy[locale]}</p>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="grid gap-8 lg:grid-cols-2">
                <Card className="space-y-5 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {philosophy.eyebrow}
                  </p>
                  <h2 className="text-2xl font-semibold text-white md:text-3xl">{philosophy.heading}</h2>
                  <p className="text-sm text-white/70">{philosophy.description}</p>
                  <ul className="space-y-3 text-sm text-white/75">
                    {philosophy.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </Card>

                <Card className="space-y-5 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {scope.eyebrow}
                  </p>
                  <h2 className="text-2xl font-semibold text-white md:text-3xl">{scope.heading}</h2>
                  <p className="text-sm text-white/70">{scope.description}</p>
                  <ul className="space-y-3 text-sm text-white/75">
                    {scope.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  {scope.note ? <p className="text-xs text-white/55">{scope.note}</p> : null}
                </Card>
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {team.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{team.heading}</h2>
                <p className="text-lg text-white/70">{team.description}</p>
              </div>

              <div className="mt-12 grid gap-8 md:grid-cols-3">
                {teamCards.map((member) => (
                  <Card
                    key={member.name}
                    className="group space-y-4 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass"
                  >
                    <div className="flex flex-col items-start gap-4">
                      <div className="flex h-24 w-24 items-center justify-center rounded-full border border-white/10 bg-white/5">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="h-14 w-14 object-contain opacity-80"
                          loading="lazy"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white">{member.name}</h3>
                        <p className="text-sm text-brand-highlight/70">{member.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-white/70">{member.description}</p>
                  </Card>
                ))}
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {values.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{values.heading}</h2>
                <p className="text-lg text-white/70">{values.description}</p>
              </div>

              <div className="mt-12 grid gap-6 md:grid-cols-2">
                {values.values.map((value, index) => {
                  const Icon = valueIcons[index] ?? Compass;
                  return (
                    <Card
                      key={value.title}
                      className="group space-y-4 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-6 shadow-glass"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">{value.title}</h3>
                      <p className="text-sm text-white/70">{value.description}</p>
                    </Card>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <Card className="rounded-2xl border-white/15 bg-black/70 p-8 shadow-glass">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {locationSection.eyebrow}
                </p>
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-4xl">{locationSection.heading}</h2>
                <p className="mt-4 text-sm text-white/70">{locationSection.description}</p>
              </Card>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                    {closing.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto mt-4 max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                  >
                    {closing.title}
                  </Display>
                  <p className="mx-auto mt-4 max-w-3xl text-center text-lg text-white/70">
                    {closing.description}
                  </p>
                  <p className="mt-6 text-sm uppercase tracking-[0.3em] text-white/50">{closing.lead}</p>
                  <ul className="mx-auto mt-6 grid max-w-3xl gap-3 text-left text-sm text-white/70 sm:grid-cols-3 sm:gap-4">
                    {closing.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                      >
                        <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition-colors duration-300 group-hover:bg-brand-highlight" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      as="button"
                      onClick={() => window.navigateToConsultation?.()}
                      trackCta={{ text: closing.primaryCta, section: 'about_closing', variant: 'primary' }}
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                    >
                      {closing.primaryCta}
                    </Button>
                    <Button
                      as="a"
                      href={localizedPath('/projects')}
                      trackCta={{ text: closing.secondaryCta, section: 'about_closing', variant: 'secondary' }}
                      variant="secondary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                    >
                      {closing.secondaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </div>
  );
}
